import React, { useEffect, useState } from "react";
import submit from "submit-form";
import isEmpty from "lodash/isEmpty";
import getAvatarUrl from "../../../utils/get-avatar-url";
import {
  hasNoAthletes,
  isActiveUserTheCoach,
  isTestAthlete,
} from "../../../utils/user-helpers";
import NoAthletesView from "./components/NoAthletesView";
import { Header, SubHeader } from "./components/Header";
import { S3 } from "../../../constants";
import SvgIcon from "../../../parts/SvgIcon";
import ConfirmationModal from "../../../parts/modals/ConfirmationModal";
import InlineSvgIcon from "../../../parts/InlineSvgIcon";
import UnacknowledgedAthlete from "./components/UnacknowledgedAthlete";

const resendInvite = (e, email) => {
  e.preventDefault();

  // this endpoint is in the athletes.controller
  submit(`/coaching-portal/athletes/resend-invite`, {
    method: "POST",
    body: {
      email,
    },
  });
};

const cancelInvite = (e, email, organizationCoach) => {
  e.preventDefault();
  let body = { email };
  if (organizationCoach) {
    body = { ...body, organizationCoachId: organizationCoach };
  }
  // this endpoint is in the athletes.controller
  submit(`/coaching-portal/athletes/cancel-invite`, {
    method: "POST",
    body,
  });
};

const deleteGroup = (e, groupName) => {
  e.preventDefault();
  submit(`/coaching-portal/athletes/delete-group`, {
    method: "POST",
    body: {
      groupName,
    },
  });
};

const MENU_OPTIONS = (
  athleteId,
  nutritionCondition = false,
  reportsCondition = false,
  isInactive = true,
  organizationCoach,
  organizationRoleName,
  athleteIsFromOtherOrganizationCoach
) => {
  const menu = [];
  const organizationQuery =
    athleteIsFromOtherOrganizationCoach ?? organizationCoach;

  menu.push({
    label: "Profile",
    iconSrc: "/img/new-icons/Profile-dark.svg",
    url: organizationQuery
      ? `/athlete-portal/profile/?athlete=${athleteId}&section=dashboard&organizationCoach=${organizationQuery}`
      : `/athlete-portal/profile/?athlete=${athleteId}&section=dashboard`,
  });
  if (organizationRoleName !== "Nutritionist") {
    menu.push({
      label: "Programs",
      iconSrc: "/img/new-icons/dumbbell-dark.svg",

      url: organizationQuery
        ? `/athlete-portal/programs/?athlete=${athleteId}&organizationCoach=${organizationQuery}`
        : `/athlete-portal/programs/?athlete=${athleteId}`,
    });
  }

  if (!isInactive && organizationRoleName !== "Nutritionist") {
    menu.push({
      label: "Calendar",
      iconSrc: "/img/new-icons/Calendar-Dark.svg",

      url: organizationQuery
        ? `/athlete-portal/calendar/?athlete=${athleteId}&organizationCoach=${organizationQuery}`
        : `/athlete-portal/calendar/?athlete=${athleteId}`,
    });
  }

  if (nutritionCondition) {
    menu.push({
      label: "Nutrition",
      iconSrc: "/img/new-icons/Nutrition-dark.svg",

      url: organizationQuery
        ? `/athlete-portal/nutrition/?athlete=${athleteId}&organizationCoach=${organizationQuery}`
        : `/athlete-portal/nutrition/?athlete=${athleteId}`,
    });
  }

  if (reportsCondition) {
    menu.push({
      label: "Reports",
      iconSrc: "/img/new-icons/Report-dark.svg",

      url: organizationQuery
        ? `/athlete-portal/metrics/exercise-history?athlete=${athleteId}&organizationCoach=${organizationQuery}`
        : `/athlete-portal/metrics/exercise-history?athlete=${athleteId}`,
    });
  }

  return menu;
};

const INVITED_MENU_OPTIONS = (athlete, organizationCoach) => {
  const menu = [
    {
      label: athlete.notRegistered ? "User not registered" : "Registered User",
      iconSrc: athlete.notRegistered
        ? "/img/new-icons/user-registered.svg"
        : "/img/new-icons/user-registered.svg",
      url: "#",
    },
    {
      label: "Coaching Request (pending)",
      iconSrc: "/img/new-icons/coaching-request.svg",
      url: "#",
    },
    {
      label: "Re-send invitation",
      iconSrc: "/img/new-icons/resend-invite.svg",
      url: "#",
      action: (e) => resendInvite(e, athlete.email),
    },
    {
      label: "Cancel Invite",
      iconSrc: "/img/new-icons/cancel-invite.svg",
      url: "#",
      action: (e) => cancelInvite(e, athlete.email, organizationCoach),
    },
    {
      label: "Help",
      iconSrc: "/img/new-icons/help.svg",
      url: "/dashboard/support/contact",
    },
  ];
  return menu;
};

const getAthleteMenu = (
  athlete,
  pending,
  organizationCoach,
  organizationRoleName,
  athleteIsFromOtherOrganizationCoach
) => {
  if (pending && !(athlete.status && athlete.status.inactive)) {
    return INVITED_MENU_OPTIONS(athlete, organizationCoach);
  }
  return MENU_OPTIONS(
    athlete._id,
    Boolean(athlete.status && !athlete.status.inactive),
    Boolean(athlete.status && !athlete.status.inactive),
    athlete.status && athlete.status.inactive,
    organizationCoach,
    organizationRoleName,
    athleteIsFromOtherOrganizationCoach
  );
};

const MobileAthlete = ({
  athlete,
  pending = false,
  activeUser,
  selectedAthlete,
  handleSelectedAthlete,
  theme,
  organizationCoach,
  organizationRoleName,
}) => {
  const [hoverActive, setHoverActive] = useState(false);
  const isTestUser = isTestAthlete(athlete.email, activeUser.email);
  const isAthleteInactive = athlete.status ? athlete.status.inactive : false;

  //_id of the coach, if we are searching by all the athletes of the organization
  const athleteIsFromOtherOrganizationCoach =
    activeUser.organizationId &&
    athlete.coachId &&
    !isActiveUserTheCoach(activeUser._id, athlete.coachId)
      ? athlete.coachId
      : undefined;

  return (
    <article
      className="only-show-mobile"
      onMouseEnter={() => {
        // Only if the object window is accesible, avoid the hover handler for mobile version
        if (typeof window !== undefined) {
          if (window.innerWidth > 960) return setHoverActive(true);
          return null;
        }
        //   If window is not accesible, just leave it as desktop to avoid error
        return setHoverActive(true);
      }}
      onMouseLeave={() => {
        // Only if the object window is accesible, avoid the hover handler for mobile version
        if (typeof window !== undefined) {
          if (window.innerWidth > 960) return setHoverActive(false);
          return null;
        }
        //   If window is not accesible, just leave it as desktop to avoid error
        return setHoverActive(false);
      }}
    >
      {isTestUser && !(hoverActive || selectedAthlete === athlete._id) && (
        <div className="test-account-label">
          <span>Test Account</span>
        </div>
      )}
      {/* Added the second condition so as to show the athlete options when the athlete is selected */}
      {hoverActive || selectedAthlete === athlete._id ? (
        <ul>
          {getAthleteMenu(
            athlete,
            pending,
            organizationCoach,
            organizationRoleName,
            athleteIsFromOtherOrganizationCoach
          ).map(({ label, iconSrc, url, action = null }, idx) => {
            if (label === "Reports") {
              return (
                <>
                  <a
                    className="mobile-hidden"
                    href={url}
                    key={`${idx}-desktop`}
                    onClick={(e) => {
                      if (!action) return;
                      action(e);
                    }}
                  >
                    <SvgIcon src={iconSrc} alt={`icon-${idx}`} />
                    <p>{label}</p>
                  </a>
                  <a
                    className="only-show-mobile"
                    href={`/athlete-portal/metrics/reports-menu?athlete=${athlete._id}`}
                    key={`${idx}-mobile`}
                    onClick={(e) => {
                      if (!action) return;
                      action(e);
                    }}
                  >
                    <SvgIcon src={iconSrc} alt={`icon-${idx}`} />
                    <p>{label}</p>
                  </a>
                </>
              );
            }
            return (
              <a
                href={url}
                key={idx}
                onClick={(e) => {
                  if (!action) return;
                  action(e);
                }}
              >
                <SvgIcon src={iconSrc} alt={`icon-${idx}`} />
                <p>{label}</p>
              </a>
            );
          })}
        </ul>
      ) : (
        <div
          onClick={() => {
            //     Only if the window object is accessible, and it is mobile, will trigger the selected handler
            if (typeof window !== undefined && window.innerWidth <= 960) {
              return handleSelectedAthlete(athlete._id);
            }
            return;
          }}
        >
          <div
            data-default={
              athlete.avatar && athlete.avatar.s3Path ? undefined : true
            }
          >
            {athlete.avatar && athlete.avatar.s3Path ? (
              <img
                src={`${S3.PATH}${athlete.avatar.s3Path}`}
                alt={`${athlete.name} profile picture`}
                loading="lazy"
              />
            ) : (
              <SvgIcon src="/img/new-icons/user-circle.svg" />
            )}
          </div>
          <div
            className={
              pending && athlete && athlete.email && athlete.email.length > 23
                ? "pending-email-div"
                : ""
            }
          >
            <span
              className={
                pending && athlete && athlete.email && athlete.email.length > 23
                  ? "pending-email-text"
                  : ""
              }
            >
              <b>{athlete.name || athlete.email}</b>
            </span>
            {!pending && athlete.groups && athlete.groups.length ? (
              <span>{athlete.groups.join(" | ")}</span>
            ) : null}
          </div>
        </div>
      )}
    </article>
  );
};

const Athlete = ({
  athlete,
  pending = false,
  activeUser,
  selectedAthlete,
  handleSelectedAthlete,
  organizationCoach,
  organizationRoleName,
}) => {
  const [hoverActive, setHoverActive] = useState(false);
  const isTestUser = isTestAthlete(athlete.email, activeUser.email);
  const isAthleteInactive = athlete.status ? athlete.status.inactive : false;

  //_id of the coach, if we are searching by all the athletes of the organization
  const athleteIsFromOtherOrganizationCoach =
    activeUser.organizationId &&
    athlete.coachId &&
    !isActiveUserTheCoach(activeUser._id, athlete.coachId)
      ? athlete.coachId
      : undefined;

  return (
    <article className="mobile-hidden athlete-card-wrapper">
      {isTestUser && !(hoverActive || selectedAthlete === athlete._id) && (
        <div className="test-account-label">
          <span>Test Account</span>
        </div>
      )}
      {/* Added the second condition so as to show the athlete options when the athlete is selected */}
      <ul>
        {getAthleteMenu(
          athlete,
          pending,
          organizationCoach,
          organizationRoleName,
          athleteIsFromOtherOrganizationCoach
        ).map(({ label, iconSrc, url, action = null }, idx) => {
          if (label === "Reports") {
            return (
              <>
                <a
                  className="mobile-hidden"
                  href={url}
                  key={`${idx}-desktop`}
                  onClick={(e) => {
                    if (!action) return;
                    action(e);
                  }}
                >
                  <SvgIcon src={iconSrc} alt={`icon-${idx}`} />
                  <p>{label}</p>
                </a>
                <a
                  className="only-show-mobile"
                  href={`/athlete-portal/metrics/reports-menu?athlete=${athlete._id}`}
                  key={`${idx}-mobile`}
                  onClick={(e) => {
                    if (!action) return;
                    action(e);
                  }}
                >
                  <SvgIcon src={iconSrc} alt={`icon-${idx}`} />
                  <p>{label}</p>
                </a>
              </>
            );
          }
          return (
            <a
              href={url}
              key={idx}
              onClick={(e) => {
                if (!action) return;
                action(e);
              }}
            >
              <SvgIcon src={iconSrc} alt={`icon-${idx}`} />
              <p>{label}</p>
            </a>
          );
        })}
      </ul>
      <div
        onClick={() => {
          //     Only if the window object is accessible, and it is mobile, will trigger the selected handler
          if (typeof window !== undefined && window.innerWidth <= 960) {
            return handleSelectedAthlete(athlete._id);
          }
          return;
        }}
      >
        <div
          data-default={
            athlete.avatar && athlete.avatar.s3Path ? undefined : true
          }
        >
          {athlete.avatar && athlete.avatar.s3Path ? (
            <img
              src={`${S3.PATH}${athlete.avatar.s3Path}`}
              alt={`${athlete.name} profile picture`}
              loading="lazy"
            />
          ) : (
            <SvgIcon src="/img/new-icons/user-circle.svg" />
          )}
        </div>
        <div
          className={
            pending && athlete && athlete.email && athlete.email.length > 23
              ? "pending-email-div"
              : ""
          }
        >
          <span
            className={
              pending && athlete && athlete.email && athlete.email.length > 23
                ? "pending-email-text"
                : ""
            }
          >
            <b>{athlete.name || athlete.email}</b>
          </span>
          {!pending && athlete.groups && athlete.groups.length ? (
            <p className="p6">{athlete.groups.join(" | ")}</p>
          ) : null}
        </div>
      </div>
      {/* {hoverActive || selectedAthlete === athlete._id ? (
      ) : (
      )} */}
    </article>
  );
};

const GroupSelector = ({ athleteGroup, athletesLength, search, query }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleConfirm = (e) => {
    deleteGroup(e, athleteGroup.name);
    setIsModalOpen(false);
  };
  return (
    <hgroup className="group-content">
      {/* desktop */}
      <div className="force-mobile-hidden">
        <h3>{athleteGroup.name}</h3>
        <p className="p5">{athleteGroup.description}</p>
      </div>
      {/* mobile */}
      <div className="force-only-show-mobile">
        <div>
          <h3>{athleteGroup.name}</h3>
          <p className="p5">{athleteGroup.description}</p>
        </div>
        <div>
          <b>
            ({athletesLength} member{athletesLength === 1 ? "" : "s"})
          </b>
        </div>
      </div>
      <div>
        <div className="mobile-hidden">
          <b>
            ({athletesLength} member{athletesLength === 1 ? "" : "s"})
          </b>
        </div>
        <a
          href={`/coaching-portal/athletes/${search}&modal[type]=members-group`}
          role="button"
          data-button-type="secondary"
          data-icon
        >
          <SvgIcon src="/img/new-icons/manage.svg" alt="manage icon" />
          Manage
        </a>
        <button
          data-button-type="secondary"
          data-action="delete"
          // onClick={(e) => deleteGroup(e, query.filterGroup)}
          onClick={() => setIsModalOpen(true)}
          data-icon
        >
          <img src="/img/new-icons/red-trash.svg" alt="delete icon" />
          Delete Group
        </button>
        <ConfirmationModal
          isOpen={isModalOpen}
          title="Delete Group"
          contentText={`Are you sure you want to delete ${athleteGroup.name} group? \n If you delete the group, you won’t be able to recover the data back.`}
          cancelButtonText="Cancel"
          cancelButtonAction={handleCancel}
          confirmButtonText="Delete"
          confirmButtonAction={handleConfirm}
          icon="delete"
        />
      </div>
      {/* ADDED COMPONENT FOR MOBILE */}
      {/* <div className="group-delete-manage only-show-mobile">
        <div className="group-manage">
          <a
            href={`/coaching-portal/athletes/${search}&modal[type]=members-group`}
          >
            <img src="/img/svg/edit-pencil.svg" alt="manage" />
            Manage
          </a>
        </div>
        <div
          className="group-delete"
          onClick={(e) => deleteGroup(e, query.filterGroup)}
        >
          Delete group <img src="/img/svg/trash-alt.svg" alt="trash" />
        </div>
      </div> */}
      {/* ---------------------------------------------------- */}
      {/* <div
        className="group-delete mobile-hidden"
        onClick={(e) => deleteGroup(e, query.filterGroup)}
      >
        Delete group <img src="/img/svg/trash-alt.svg" alt="trash" />
      </div> */}
    </hgroup>
  );
};

const View = (props, context) => {
  const [athletesState, setAthletesState] = useState({
    welcomeVideoShownCoach: false,
    activeToggle: isEmpty(context.locals.query)
      ? "all"
      : context.locals.query.filters &&
        context.locals.query.filters["status.inactive"]
      ? context.locals.query.filters["status.inactive"]
      : "",
  });

  // Added this state to replace hover with click for mobile version
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const { referAsClients } = context.locals.activeUser;
  useEffect(() => {
    const { activeUser } = context.locals;
    // the property was moved into `welcomeVideos`. But some users still have this property
    // out of this new object. So we need to check both
    if (
      !activeUser.welcomeVideoShownCoach &&
      (!activeUser.welcomeVideos ||
        !activeUser.welcomeVideos.welcomeVideoShownCoach)
    ) {
      setAthletesState((state) => {
        return Object.assign({}, state, { welcomeVideoShownCoach: true });
      });
    }
  }, []);

  const closeWelcomeForCoach = () => {
    setAthletesState((state) => {
      return Object.assign({}, state, { welcomeVideoShownCoach: false });
    });
    submit("/actions/user/video-shown", {
      method: "POST",
      body: { type: "welcomeVideoShownCoach" },
    });
  };

  const updateToggleActive = (value) => {
    setAthletesState((state) => {
      return Object.assign({}, state, { activeToggle: value });
    });
  };

  const handleSelectedAthlete = (id) => {
    setSelectedAthlete(id);
  };

  const { locals } = context;
  const {
    athletes = [],
    query,
    activeUser,
    coachOrganizationInformation,
    organizationRoleName,
    search,
    allAthletesFromOrganization,
    theme,
  } = locals;
  //Get the name of the organization's role of the current user

  //The user is allow to see al the athletes of the coaches
  const isAllowToViewAllOrganizationsCoaches =
    organizationRoleName === "Owner" ||
    organizationRoleName === "Admin" ||
    organizationRoleName === "Nutritionist";

  const { activeToggle } = athletesState;
  const athletesGroups = coachOrganizationInformation
    ? coachOrganizationInformation.athletesGroups
    : activeUser.athletesGroups;
  const athletesGroupsArray = athletesGroups || [];

  const organizationCoach = query.organizationCoach || undefined;

  const athleteGroup =
    query && query.filterGroup && query.filterGroup
      ? athletesGroupsArray.find((elem) => elem.name === query.filterGroup)
      : null;

  const activeAthletes = athletes.filter((athlete) => {
    const unacknowledged = athlete.isGymMember && !athlete.acknowledged;
    return !athlete.invited && !athlete.status.inactive && !unacknowledged;
  });

  const pendingAthletes = athletes.filter((athlete) => athlete.invited);

  const inactiveAthletes = athletes.filter((athlete) => {
    return athlete.status && athlete.status.inactive;
  });

  // here we need to reformat the array of athletes from the organization
  // because they are being retreived from the db under different fields names
  const athletesToAcknowledge = allAthletesFromOrganization
    ? reformattedAthletes(allAthletesFromOrganization).filter((athlete) => {
        return athlete.isGymMember && !athlete.acknowledged;
      })
    : athletes.filter((athlete) => {
        return athlete.isGymMember && !athlete.acknowledged;
      });

  const filterMode = query.filters ? query.filters["status.inactive"] : false;

  const noAthletesCoachName =
    query.name || coachOrganizationInformation?.name || "this user";

  if (
    hasNoAthletes(activeUser) &&
    (!isAllowToViewAllOrganizationsCoaches ||
      !allAthletesFromOrganization.length)
  ) {
    return <NoAthletesView />;
  }

  return (
    <main className="athlete-list">
      <SubHeader
        activeToggle={activeToggle}
        updateToggleActive={updateToggleActive}
        referAsClients={referAsClients}
        locals={locals}
        isAllowToViewAllOrganizationsCoaches={
          isAllowToViewAllOrganizationsCoaches
        }
      />
      <Header
        athletesGroups={athletesGroups}
        referAsClients={referAsClients}
        isAllowToViewAllOrganizationsCoaches={
          isAllowToViewAllOrganizationsCoaches
        }
        query={query}
      />
      {athleteGroup ? (
        <GroupSelector
          athleteGroup={athleteGroup}
          athletesLength={athletes.length}
          search={search}
          query={query}
        />
      ) : null}

      {athletes.length <= 0 && (
        <p className="no-athlete">
          No {referAsClients ? "clients" : "athletes"} have been found for "
          {query.name}"
        </p>
      )}
      {["all", false].includes(filterMode) &&
      athletesToAcknowledge &&
      athletesToAcknowledge.length ? (
        <>
          <section>
            <h2 className="athlete-list-title">
              New {referAsClients ? "Clients" : "Athletes"}
            </h2>
            {athletesToAcknowledge.map((athlete, idx) => {
              return (
                <UnacknowledgedAthlete
                  key={idx}
                  athlete={athlete}
                  activeUser={activeUser}
                />
              );
            })}
          </section>
        </>
      ) : null}

      {["all", false].includes(filterMode) &&
      activeAthletes &&
      activeAthletes.length ? (
        <>
          <section>
            <h2 className="athlete-list-title">
              Active {referAsClients ? "Clients" : "Athletes"}
            </h2>
            {activeAthletes.map((athlete, idx) => {
              return (
                <>
                  <MobileAthlete
                    key={`${athlete._id}-mobile`}
                    athlete={athlete}
                    activeUser={context.locals.activeUser}
                    selectedAthlete={selectedAthlete}
                    handleSelectedAthlete={handleSelectedAthlete}
                    organizationCoach={organizationCoach}
                    organizationRoleName={organizationRoleName}
                  />
                  <Athlete
                    key={`${athlete._id}-desktop`}
                    athlete={athlete}
                    activeUser={context.locals.activeUser}
                    selectedAthlete={selectedAthlete}
                    handleSelectedAthlete={handleSelectedAthlete}
                    organizationCoach={organizationCoach}
                    organizationRoleName={organizationRoleName}
                  />
                </>
              );
            })}
          </section>
        </>
      ) : null}

      {["all", true].includes(filterMode) &&
      inactiveAthletes &&
      inactiveAthletes.length ? (
        <section>
          <h2>Inactive {referAsClients ? "Clients" : "Athletes"}</h2>
          {inactiveAthletes.map((athlete, idx) => {
            return (
              <>
                <MobileAthlete
                  key={`${athlete._id}-mobile`}
                  athlete={athlete}
                  pending
                  activeUser={context.locals.activeUser}
                  selectedAthlete={selectedAthlete}
                  handleSelectedAthlete={handleSelectedAthlete}
                  organizationCoach={organizationCoach}
                  organizationRoleName={organizationRoleName}
                />
                <Athlete
                  key={`${athlete._id}-desktop`}
                  athlete={athlete}
                  pending
                  activeUser={context.locals.activeUser}
                  selectedAthlete={selectedAthlete}
                  handleSelectedAthlete={handleSelectedAthlete}
                  organizationCoach={organizationCoach}
                  organizationRoleName={organizationRoleName}
                />
              </>
            );
          })}
        </section>
      ) : null}
      {["all", false].includes(filterMode) &&
      pendingAthletes &&
      pendingAthletes.length ? (
        <section>
          <h2>Pending Invites</h2>
          {pendingAthletes.map((athlete, idx) => {
            return (
              <>
                <MobileAthlete
                  key={`${athlete._id}-mobile`}
                  athlete={athlete}
                  pending
                  activeUser={context.locals.activeUser}
                  selectedAthlete={selectedAthlete}
                  handleSelectedAthlete={handleSelectedAthlete}
                  organizationCoach={organizationCoach}
                  organizationRoleName={organizationRoleName}
                />
                <Athlete
                  key={`${athlete._id}-desktop`}
                  athlete={athlete}
                  pending
                  activeUser={context.locals.activeUser}
                  selectedAthlete={selectedAthlete}
                  handleSelectedAthlete={handleSelectedAthlete}
                  organizationCoach={organizationCoach}
                  organizationRoleName={organizationRoleName}
                />
              </>
            );
          })}
        </section>
      ) : null}
    </main>
  );
};

export default View;

const reformattedAthletes = (athletesToReformat) => {
  const athletes = athletesToReformat.map(
    ({
      athleteName,
      athleteAvatar,
      athleteIsGymMember,
      athleteAcknowledged,
      athleteEmail,
      athleteId,
      coachId,
      coachName,
    }) => ({
      _id: athleteId,
      coach: { _id: coachId, name: coachName },
      name: athleteName,
      email: athleteEmail,
      avatar: athleteAvatar,
      isGymMember: athleteIsGymMember,
      acknowledged: athleteAcknowledged,
    })
  );
  return athletes;
};
